/**
 * Keycloak/NextAuth web auth path — split out from `useAuth.ts` so the
 * cloud bundle never pulls in `next-auth/react`.
 *
 * `next-auth/react/index.js` runs `parseUrl(process.env.NEXTAUTH_URL)`
 * at module top level. Cloud builds don't set NEXTAUTH_URL, so a static
 * `import { useSession } from 'next-auth/react'` anywhere on a cloud
 * server-rendered page graph crashes SSR with `TypeError: Invalid URL`.
 *
 * Same fix pattern as `KeycloakAuthShell` in `auth-wrapper.tsx` and
 * `KeycloakSignInModal` in `sign-in-modal-email-only.tsx`: keep all
 * `next-auth/react`-touching code in a module that is only loaded when
 * `isKeycloak === true`. The `useAuth()` dispatcher lazy-requires this
 * module from inside its Keycloak-web branch, so the cloud bundle
 * never evaluates `next-auth/react`'s top-level code.
 */
import { useCallback, useMemo } from 'react';

import {
  signIn as nextAuthSignIn,
  signOut as nextAuthSignOut,
  useSession,
} from 'next-auth/react';

import { useOsLocaleValue } from '@/providers/os-locale-context.utils';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import {
  buildKeycloakEndSessionUrl,
  getBrowserKeycloakIssuerUrl,
} from '@/utils/keycloakConfig';
import { toKeycloakUiLocales } from '@heidi/utils/i18n';

import type { AuthState, AuthUser } from './useAuth';

type SessionWithToken = {
  user?: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
    /** Enriched by the NextAuth `session` callback from JWT `sub`. */
    id?: string | null;
  } | null;
  accessToken?: string;
  idToken?: string;
  error?: string;
};

export function useAuthKeycloakWebPath(): AuthState {
  const { data, status } = useSession();
  const session = data as SessionWithToken | null;

  const isLoading = status === 'loading';
  const isAuthenticated = status === 'authenticated';
  const hasRefreshError = session?.error === 'RefreshAccessTokenError';
  const accessToken = session?.accessToken ?? null;

  const user = useMemo<AuthUser | null>(() => {
    if (!session?.user) return null;
    const name = session.user.name ?? '';
    const [given = null, ...rest] = name ? name.split(' ') : [];
    const family = rest.length > 0 ? rest.join(' ') : null;
    return {
      // Enriched in the NextAuth `session` callback in `authOptions.ts`
      // — Keycloak's `sub` claim becomes `session.user.id`, matching
      // the Tauri path's `User.profile.sub` so the unified contract
      // returns a stable id on both runtimes.
      id: session.user.id ?? null,
      email: session.user.email ?? null,
      givenName: given || null,
      familyName: family,
    };
  }, [session]);

  const idToken = session?.idToken ?? null;
  const getToken = useCallback(() => Promise.resolve(accessToken), [accessToken]);
  // Pre-auth locale (browser/OS) so the Keycloak login page renders in the same
  // language as the app. ui_locales only takes effect once the realm enables
  // internationalization (ml-scribe realm-onprem.json); omitted when unresolved
  // so Keycloak falls back to Accept-Language.
  const osLocale = useOsLocaleValue();
  const login = useCallback(() => {
    void nextAuthSignIn(
      'keycloak',
      undefined,
      osLocale ? { ui_locales: toKeycloakUiLocales(osLocale) } : undefined
    );
  }, [osLocale]);
  const logout = useCallback(() => {
    void (async () => {
      // RP-initiated logout. NextAuth's `signOut` clears its own session
      // cookie only — the Keycloak SSO cookie on the realm host survives,
      // so the next sign-in skips the login form. Chain into Keycloak's
      // `end_session_endpoint` to invalidate the realm-side session too.
      // The Tauri path gets this for free via `oidc-client-ts`'
      // `signoutRedirect()`.
      await nextAuthSignOut({ redirect: false });
      // Shared builder sends `id_token_hint` (skips Keycloak's logout-
      // confirmation screen) with a `client_id` fallback — see
      // `buildKeycloakEndSessionUrl`.
      const url = buildKeycloakEndSessionUrl({
        issuerUrl: getBrowserKeycloakIssuerUrl(),
        idToken,
        postLogoutRedirectUri: `${window.location.origin}/`,
      });
      window.location.assign(url);
    })();
  }, [idToken]);
  const register = useCallback(() => {
    // Self-service signup is disabled in the on-prem realm. The "Register"
    // CTA should be hidden in the UI on isKeycloak builds; if a stale
    // call site reaches this path, the no-op + warning surfaces the
    // misconfiguration without breaking the page.
    // eslint-disable-next-line no-console
    console.warn(
      '[useAuth] register() is a no-op on Keycloak — realm disables self-registration'
    );
  }, []);

  return {
    isAuthenticated,
    isLoading,
    hasRefreshError,
    user,
    provider: AuthProvider.KEYCLOAK,
    getToken,
    login,
    logout,
    register,
  };
}
